import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import cloudStorageService from '../../../services/cloudStorageService';

const AccessControlPanel = ({ onRefresh }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await cloudStorageService?.getAccessControlRoles();
      setRoles(data);
    } catch (err) {
      console.error('Error loading roles:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* RBAC Overview */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={24} className="text-blue-600 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
              Role-Based Access Control (RBAC) Active
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Granular permission management with department-level restrictions and audit trail logging.
              All file access is logged and monitored for compliance.
            </p>
          </div>
        </div>
      </div>

      {/* Role Management */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Icon name="Users" size={20} />
            Access Control Roles
          </h3>
          <Button variant="default" size="sm" iconName="Plus">
            Add Role
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader" size={24} className="animate-spin text-blue-600 mx-auto" />
          </div>
        ) : (
          <div className="space-y-3">
            {roles?.map(role => (
              <div
                key={role?.id}
                className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {role?.roleName}
                      </h4>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                        {role?.userCount} users
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {role?.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {role?.permissions?.map((permission, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300"
                        >
                          <Icon name="Check" size={12} className="text-green-600" />
                          {permission}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" iconName="Edit" />
                    <Button variant="ghost" size="sm" iconName="Trash2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Department Restrictions */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-6 border border-amber-200 dark:border-amber-800">
        <div className="flex items-start gap-3">
          <Icon name="Building" size={24} className="text-amber-600 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">
              Department-Level Access Restrictions
            </h4>
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
              Access to client data is restricted by department. HR staff can only access HR documents,
              Finance staff can only access financial records, etc.
            </p>
            <Button variant="outline" size="sm" iconName="Settings">
              Configure Department Access
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessControlPanel;